import type {
  KeyPoseSkeletonSnapshot,
  KeyPoseSourcePosePayload,
  KeyPoseTransformSnapshot,
} from './types';

export function cloneKeyPoseSkeletonSnapshot(
  skeletonSnapshot: KeyPoseSkeletonSnapshot,
  frame = skeletonSnapshot.frame,
): KeyPoseSkeletonSnapshot {
  const transformsByName: Record<string, KeyPoseTransformSnapshot> = {};

  for (const [objectName, transform] of Object.entries(
    skeletonSnapshot.transformsByName,
  )) {
    transformsByName[objectName] = {
      position: [...transform.position],
      quaternion: [...transform.quaternion],
      scale: [...transform.scale],
    };
  }

  return {
    ...skeletonSnapshot,
    frame,
    transformsByName,
  };
}

export function cloneKeyPoseSourcePose(
  sourcePose: KeyPoseSourcePosePayload,
  frame = sourcePose.frame,
): KeyPoseSourcePosePayload {
  return {
    ...sourcePose,
    frame,
    localJointRot: sourcePose.localJointRot.map((axisAngle) => [
      ...axisAngle,
    ]),
    rootPosition: [...sourcePose.rootPosition],
    smoothRoot2d: [...sourcePose.smoothRoot2d],
  };
}

export function getKeyPosePersistenceKey(
  sceneID: string,
  referenceName: string,
  timelineIndex: number,
) {
  return [sceneID, referenceName, timelineIndex].join('::');
}

export function createKeyPoseGenerationRunId(
  referenceName: string,
  motionID: string,
) {
  const randomID = globalThis.crypto?.randomUUID?.() ?? String(Date.now());
  return `${referenceName}:${motionID}:${randomID}`;
}

export function getErrorMessage(error: unknown, fallbackMessage: string) {
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message.trim().length > 0
  ) {
    return error.message;
  }

  return fallbackMessage;
}

export function wait(ms: number, signal?: AbortSignal) {
  if (signal?.aborted) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const timeoutId = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeoutId);
        resolve();
      },
      { once: true },
    );
  });
}
